import type { Option } from "@tento-lona/sheets";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { DecorationRenderController } from "./decoration-render-controller.ts";
import {
  DecorationMeasuredFrame,
  DecorationPosition,
  DecorationRenderer,
  DecorationRendererContext,
} from "./decoration-renderer.ts";
import { $$ } from "@tento-ui/fastdom";

export class FixedFrameDecorationRenderer implements DecorationRenderer {
  #decorations: Map<DecorationPosition, HTMLElement> = new Map();
  #prevFrames: Map<string, DecorationRenderController.Frame> = new Map();
  #resizeObserver: Option<ResizeObserver> = null;
  #resizePending = false;
  #observedCells = new Set<Element>();

  readonly #context: DecorationRendererContext;
  constructor(context: DecorationRendererContext) {
    this.#context = context;
  }

  #getResizeObserver(): ResizeObserver {
    if (!this.#resizeObserver) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.#resizePending) return;
        this.#resizePending = true;
        $$.mutate(() => {
          this.#resizePending = false;
          if (!this.#resizeObserver) return;
          this.rebind();
        });
      });
    }
    return this.#resizeObserver;
  }

  set(decorations: Map<DecorationPosition, HTMLElement>): void {
    this.#decorations = decorations;
    this.rebind();
  }

  rebind(): void {
    const desiredIds = new Set<string>();
    const measuredFramesById = new Map<
      string,
      DecorationRenderController.Frame
    >();
    const elementsById = new Map<string, HTMLElement>();

    for (const [position, $element] of this.#decorations) {
      const id = this.#decorationId(position);
      desiredIds.add(id);
      elementsById.set(id, $element);

      const measured = this.#context.measureFrame(position);
      if (!measured) continue;

      const adjusted = this.#adjustMeasuredFrame(position, measured);

      measuredFramesById.set(id, {
        id,
        rowId: adjusted.rowId,
        anchorColumn: adjusted.anchorColumn,
        xPx: adjusted.xOffsetPx,
        yPx: adjusted.yOffsetPx,
        widthPx: adjusted.widthPx,
        heightPx: adjusted.heightPx,
      });
    }

    const plan = DecorationRenderController.computePlan(
      desiredIds,
      measuredFramesById,
      this.#prevFrames,
    );

    for (const op of plan.ops) {
      switch (op.type) {
        case "dismiss": {
          this.#context.fixedChild.dismiss(op.id, "instant");
          break;
        }
        case "reveal": {
          const $element = elementsById.get(op.frame.id);
          if (!$element) break;
          this.#revealFrame($element, op.frame);
          break;
        }
        case "update": {
          const $element = elementsById.get(op.frame.id);
          if (!$element) break;
          if (!this.#updateFrame($element, op.frame)) {
            this.#revealFrame($element, op.frame);
          }
          break;
        }
      }
    }

    this.#prevFrames = plan.nextFrames;

    const nextObserved = new Set<Element>();
    for (const [position] of this.#decorations) {
      for (const $el of this.#context.resolveCellTargets(position)) {
        nextObserved.add($el);
      }
    }
    const observer = this.#getResizeObserver();
    for (const $cell of this.#observedCells) {
      if (!nextObserved.has($cell)) observer.unobserve($cell);
    }
    for (const $cell of nextObserved) {
      if (!this.#observedCells.has($cell)) observer.observe($cell);
    }
    this.#observedCells = nextObserved;
  }

  clear(): void {
    for (const [id] of this.#prevFrames) {
      this.#context.fixedChild.dismiss(id, "instant");
    }
    this.#prevFrames.clear();
    this.#decorations.clear();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#resizePending = false;
    this.#observedCells.clear();
  }

  #applyFrameStyles(
    $element: HTMLElement,
    frame: DecorationRenderController.Frame,
  ): void {
    $element.style.width = `${frame.widthPx}px`;
    $element.style.height = `${Math.round(frame.heightPx)}px`;
    $element.setAttribute("data-inset-top", String(Math.round(frame.yPx)));
  }

  #revealFrame(
    $element: HTMLElement,
    frame: DecorationRenderController.Frame,
  ): void {
    this.#applyFrameStyles($element, frame);

    this.#context.fixedChild.reveal(
      frame.id,
      $element,
      {
        x: frame.anchorColumn,
        y: frame.rowId,
      },
      {
        animate: false,
        inset: {
          left: frame.xPx,
          top: frame.yPx,
        },
        widthBehavior: frame.widthPx,
      },
    );
  }

  #updateFrame(
    $element: HTMLElement,
    frame: DecorationRenderController.Frame,
  ): boolean {
    this.#applyFrameStyles($element, frame);

    const positioned = this.#context.fixedChild.repositionFixedChild(
      frame.id,
      frame.anchorColumn,
      frame.xPx,
    );
    const insetUpdated = this.#context.fixedChild.updateFixedChildInset(
      frame.id,
      {
        left: frame.xPx,
        top: frame.yPx,
      },
      frame.rowId,
    );
    this.#context.fixedChild.updateFixedChildWidth(
      frame.id,
      frame.widthPx,
    );

    return positioned && insetUpdated;
  }

  #decorationId(position: DecorationPosition): string {
    const row = this.#sanitizeIdPart(position.row.toString());
    const col = this.#sanitizeIdPart(this.#columnIdPart(position.col));
    return `sheet-decoration-${row}-${col}`;
  }

  #columnIdPart(col: DecorationPosition["col"]): string {
    if (typeof col === "string") {
      return col;
    }

    if (col instanceof ColumnIndex.Key) {
      const strategy = col.strategy;
      const inner = col.inner;
      if (strategy === "offset") {
        return `offset-${String(inner?.index ?? "na")}`;
      }
      if (strategy === "pd") {
        const pd = inner?.pd?.toString() ?? "pd";
        const resolution = String(inner?.resolution ?? "na");
        return `pd-${pd}-${resolution}`;
      }
      if (strategy === "pd-tz") {
        const pd = inner?.pd?.toString() ?? "pd";
        const tz = inner?.tz?.fullname ?? inner?.tz?.toString() ?? "tz";
        return `pd-tz-${pd}-${String(tz)}`;
      }
      return strategy;
    }

    return String(col);
  }

  #sanitizeIdPart(value: string): string {
    return value.replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  #adjustMeasuredFrame(
    position: DecorationPosition,
    measured: DecorationMeasuredFrame,
  ): DecorationMeasuredFrame {
    const $cell = this.#context.resolveCell(position);
    if (!$cell) {
      return measured;
    }

    const $root = $cell.shadowRoot?.querySelector("#root");
    if (!($root instanceof HTMLElement)) {
      return measured;
    }

    const rootStyles = getComputedStyle($root);
    const marginLeftPx = this.#parsePx(rootStyles.marginInlineStart);
    const marginRightPx = this.#parsePx(rootStyles.marginInlineEnd);
    const marginTopPx = this.#parsePx(rootStyles.marginBlockStart);
    const marginBottomPx = this.#parsePx(rootStyles.marginBlockEnd);

    let xOffsetPx = measured.xOffsetPx + marginLeftPx;
    let yOffsetPx = measured.yOffsetPx + marginTopPx;
    let widthPx = measured.widthPx - marginLeftPx - marginRightPx;
    let heightPx = measured.heightPx - marginTopPx - marginBottomPx;

    if ($cell.hasAttribute("card")) {
      // Align to the card surface drawn by :host([card]) #root::before { inset: 1px 2px }
      xOffsetPx += 2;
      yOffsetPx += 1;
      widthPx -= 4;
      heightPx -= 2;
    }

    return {
      rowId: measured.rowId,
      anchorColumn: measured.anchorColumn,
      xOffsetPx,
      yOffsetPx,
      widthPx: Math.max(1, widthPx),
      heightPx: Math.max(1, heightPx),
    };
  }

  #parsePx(value: string): number {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return parsed;
  }
}
